import React, { useMemo } from 'react';

// Support both snake_case (API) and camelCase formats
interface GoalItem {
  goalId?: string;
  trackStatus?: 'on_track' | 'at_risk' | 'behind';
  dueDate?: string;
  cycleId?: string;
  cycleName?: string;
  // snake_case (from API)
  goal_id?: string;
  track_status?: 'on_track' | 'at_risk' | 'behind';
  due_date?: string;
  cycle_id?: string;
  cycle_name?: string;
  // Common
  title: string;
  progress: number;
  status: string;
  priority?: string;
}

// Helper to get value with fallback for both cases
const getGoalId = (g: GoalItem) => g.goalId || g.goal_id || '';
const getTrackStatus = (g: GoalItem) => g.trackStatus || g.track_status || 'behind';
const getCycleName = (g: GoalItem) => g.cycleName || g.cycle_name || '';

interface GoalsSummary {
  onTrackCount: number;
  atRiskCount: number;
  behindCount: number;
  totalGoals: number;
}

interface CycleOption {
  value: string;
  label: string;
}

interface GoalsProgressWidgetProps {
  goals?: GoalItem[] | { data: GoalItem[] };
  summary?: GoalsSummary;
  loading?: boolean;
  error?: string;
  cycleName?: string;
  options?: {
    showCycleSelector?: boolean;
    maxGoals?: number;
  };
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

const getStatusColor = (trackStatus: string) => {
  switch (trackStatus) {
    case 'on_track':
      return { dot: 'bg-emerald-500', text: 'text-emerald-600' };
    case 'at_risk':
      return { dot: 'bg-amber-500', text: 'text-amber-600' };
    case 'behind':
      return { dot: 'bg-red-500', text: 'text-red-600' };
    default:
      return { dot: 'bg-gray-400', text: 'text-gray-600' };
  }
};

export const GoalsProgressWidget: React.FC<GoalsProgressWidgetProps> = ({
  goals,
  summary,
  loading,
  error,
  cycleName,
  options = {},
  cycleOptions,
  selectedCycleId,
  onCycleChange,
  cycleOptionsLoading,
}) => {
  const maxGoals = options.maxGoals || 10;

  // Handle wrapped data format
  const goalsList: GoalItem[] = Array.isArray(goals) ? goals : ((goals as { data: GoalItem[] })?.data || []);

  // Get cycle name from first goal's data, or use prop, or show total count
  const displayCycleName = cycleName || (goalsList[0] ? getCycleName(goalsList[0]) : '');

  // Calculate summary from goals if not provided
  const calculatedSummary = useMemo(() => {
    if (summary) return summary;
    return {
      onTrackCount: goalsList.filter(g => getTrackStatus(g) === 'on_track').length,
      atRiskCount: goalsList.filter(g => getTrackStatus(g) === 'at_risk').length,
      behindCount: goalsList.filter(g => getTrackStatus(g) === 'behind').length,
      totalGoals: goalsList.length,
    };
  }, [goalsList, summary]);

  // Sort: on_track first, then at_risk, then behind
  const sortedGoals = useMemo(() => {
    return [...goalsList].sort((a, b) => {
      const order: Record<string, number> = { on_track: 0, at_risk: 1, behind: 2 };
      return (order[getTrackStatus(a)] || 2) - (order[getTrackStatus(b)] || 2);
    }).slice(0, maxGoals);
  }, [goalsList, maxGoals]);

  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-gray-900 text-sm">Goals</h3>
      {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
        <select
          value={selectedCycleId || ''}
          onChange={(e) => onCycleChange(e.target.value)}
          disabled={cycleOptionsLoading}
          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400 w-[200px] truncate"
        >
          {cycleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader()}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-3 bg-emerald-50 rounded-lg">
          <div className="text-2xl font-bold text-emerald-600">{calculatedSummary.onTrackCount}</div>
          <div className="text-[11px] text-gray-500 mt-1">On Track</div>
        </div>
        <div className="text-center p-3 bg-amber-50 rounded-lg">
          <div className="text-2xl font-bold text-amber-600">{calculatedSummary.atRiskCount}</div>
          <div className="text-[11px] text-gray-500 mt-1">At Risk</div>
        </div>
        <div className="text-center p-3 bg-red-50 rounded-lg">
          <div className="text-2xl font-bold text-red-600">{calculatedSummary.behindCount}</div>
          <div className="text-[11px] text-gray-500 mt-1">Behind</div>
        </div>
      </div>

      {/* Goals List */}
      <div className="flex-1 min-h-0 space-y-2.5 overflow-y-auto">
        {sortedGoals.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">No active goals</div>
        ) : (
          sortedGoals.map((goal) => {
            const colors = getStatusColor(getTrackStatus(goal));
            return (
              <div key={getGoalId(goal)} className="flex items-center gap-3">
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${colors.dot}`} />
                <span className="text-sm text-gray-700 flex-1">{goal.title}</span>
                <span className={`text-xs font-medium ${colors.text}`}>{Math.round(goal.progress)}%</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default GoalsProgressWidget;
